import { useState, type CSSProperties } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { emailSchema } from '@tabbio/utils';
import { checkEmailStatus } from '../../lib/auth-api';
import {
  getUnavailableAccountTranslationKey,
  isEmailAccountUnavailable,
  isPartnerAccount,
} from '../../lib/auth-email-status';
import { signIn } from '../../lib/auth-client';
import { openMarketingLink } from '../../lib/marketing-links';
import {
  getPostAuthRedirectPath,
  type PostAuthTarget,
} from '../../lib/auth-handoff';
import { useDialog } from '../../providers/DialogProvider';
import { useToast } from '../../hooks/useToast';
import { useLocale, type SupportedLocale } from '../../hooks/useLocale';
import { SocialLoginButton } from './SocialLoginButton';
import { getAuthErrorCopy } from '../../lib/auth-error-copy';

const LOCALE_ENTRIES: { code: SupportedLocale; short: string }[] = [
  { code: 'en', short: 'EN' },
  { code: 'ar', short: 'عربي' },
];

function LanguageSwitcher({
  locale,
  onChange,
  ariaLabel,
}: {
  locale: SupportedLocale;
  onChange: (next: SupportedLocale) => void;
  ariaLabel: string;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      style={{ display: 'inline-flex', alignItems: 'center' }}
    >
      {LOCALE_ENTRIES.map((entry, index) => {
        const isActive = entry.code === locale;
        return (
          <span key={entry.code} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {index > 0 ? (
              <span
                aria-hidden
                style={{
                  color: 'var(--text-tertiary)',
                  fontSize: 13,
                  opacity: 0.6,
                  margin: '0 2px',
                }}
              >
                /
              </span>
            ) : null}
            <button
              type="button"
              aria-pressed={isActive}
              disabled={isActive}
              onClick={() => onChange(entry.code)}
              style={{
                padding: '6px 8px',
                background: 'transparent',
                border: 0,
                color: isActive ? 'var(--text-primary)' : 'var(--text-tertiary)',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: isActive ? 500 : 500,
                cursor: isActive ? 'default' : 'pointer',
                letterSpacing: 0.2,
                transition: 'color 150ms',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = 'var(--text-secondary)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = 'var(--text-tertiary)';
                }
              }}
            >
              {entry.short}
            </button>
          </span>
        );
      })}
    </div>
  );
}

export function LoginContent({
  notice,
  postAuthTarget = 'root',
}: {
  notice?: string;
  postAuthTarget?: PostAuthTarget;
}) {
  const { t } = useTranslation('auth');
  const { locale, isRTL, changeLocale } = useLocale();
  const router = useRouter();
  const dialog = useDialog();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [showPartnerNotice, setShowPartnerNotice] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState<
    'google' | 'apple' | 'uae-pass' | null
  >(null);

  const normalizedEmail = email.trim().toLowerCase();
  const noticeText = showPartnerNotice
    ? t('login.partnerAccountNotice')
    : notice;

  const handleContinue = async () => {
    const result = emailSchema.safeParse(normalizedEmail);
    if (!result.success) {
      setError(
        result.error.errors[0]?.message || t('login.emailValidationError'),
      );
      return;
    }

    setError('');
    setCheckingEmail(true);

    try {
      const status = await checkEmailStatus(normalizedEmail);

      if (isEmailAccountUnavailable(status)) {
        setError(t(getUnavailableAccountTranslationKey(status.status)));
        return;
      }

      // Partner accounts sign in here exactly like everyone else: the notice is
      // informational and must not interrupt the flow.
      const partnerAccount = isPartnerAccount(status);
      setShowPartnerNotice(partnerAccount);

      let step: string;
      if (status.exists) {
        step = status.hasPassword ? 'password' : 'otp';
      } else {
        step = 'signup-otp';
      }

      dialog.open('auth-step', {
        email: normalizedEmail,
        step,
        hasPassword: status.hasPassword,
        partnerNotice: partnerAccount,
        postAuthTarget,
      });
    } catch (err: any) {
      setError(err?.message || t('login.emailCheckError'));
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'apple') => {
    setError('');
    setLoadingProvider(provider);
    try {
      await signIn.social({
        provider: provider as any,
        callbackURL: `${window.location.origin}${getPostAuthRedirectPath(postAuthTarget)}`,
      });
    } catch (err: any) {
      const msg = getAuthErrorCopy(err, t, t('login.socialError', { provider }));
      setError(msg);
      toast.error(msg);
    } finally {
      setLoadingProvider(null);
    }
  };

  const handleUaePass = () => {
    setError('');
    setLoadingProvider('uae-pass');
    router.push(
      postAuthTarget === 'root'
        ? '/uae-pass'
        : ({ pathname: '/uae-pass', params: { next: postAuthTarget } } as any),
    );
    setLoadingProvider(null);
  };

  const canContinue = email.trim().length > 0 && !checkingEmail;

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 440,
        marginInline: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      <div
        style={{
          position: 'fixed',
          top: 24,
          insetInlineEnd: 24,
          zIndex: 10,
        }}
      >
        <LanguageSwitcher
          locale={locale}
          onChange={(next) => {
            void changeLocale(next);
          }}
          ariaLabel={t('login.languageLabel')}
        />
      </div>

      <h1
        style={{
          fontFamily: "'Google Sans', sans-serif",
          fontSize: 32,
          fontWeight: 400,
          lineHeight: '40px',
          letterSpacing: '-0.01em',
          color: 'var(--text-primary)',
          margin: '0 0 6px',
        }}
      >
        {t('login.heading')}
      </h1>

      <p
        style={{
          fontSize: 15,
          lineHeight: '22px',
          color: 'var(--text-secondary)',
          margin: '0 0 28px',
        }}
      >
        {t('login.subtitle')}
      </p>

      {noticeText ? (
        <p
          style={{
            fontSize: 13,
            lineHeight: '20px',
            color: 'var(--text-secondary)',
            margin: '0 0 16px',
          }}
        >
          {noticeText}
        </p>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        <SocialLoginButton
          provider="uae-pass"
          label={t('uaePass.signInButton')}
          onPress={handleUaePass}
          loading={loadingProvider === 'uae-pass'}
          disabled={loadingProvider !== null && loadingProvider !== 'uae-pass'}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            margin: '14px 0',
          }}
        >
          <div style={{ flex: 1, height: 1, background: 'var(--input-border)' }} />
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-tertiary)' }}>
            {t('login.or')}
          </span>
          <div style={{ flex: 1, height: 1, background: 'var(--input-border)' }} />
        </div>
        <SocialLoginButton
          provider="google"
          label={t('login.continueGoogle')}
          onPress={() => {
            void handleSocialLogin('google');
          }}
          loading={loadingProvider === 'google'}
          disabled={loadingProvider !== null && loadingProvider !== 'google'}
        />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          width: '100%',
          margin: '20px 0 16px',
        }}
      >
        <div style={{ flex: 1, height: 1, background: 'var(--input-border)' }} />
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--text-tertiary)',
          }}
        >
          {t('login.orContinueWithEmail')}
        </span>
        <div style={{ flex: 1, height: 1, background: 'var(--input-border)' }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError('');
            if (showPartnerNotice) setShowPartnerNotice(false);
          }}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void handleContinue();
            }
          }}
          placeholder={t('login.emailPlaceholder')}
          autoComplete="email"
          inputMode="email"
          style={{
            width: '100%',
            height: 52,
            borderRadius: 9999,
            border: error
              ? '1px solid #d93025'
              : inputFocused
                ? '1px solid var(--text-primary)'
                : '1px solid var(--input-border)',
            padding: '0 22px',
            fontSize: 15,
            color: 'var(--text-primary)',
            backgroundColor: 'var(--input-bg)',
            boxShadow: 'var(--gem-input-shadow)',
            outline: 'none',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
            transition: 'border-color 150ms',
          }}
        />

        {error ? (
          <p
            style={{
              margin: 0,
              fontSize: 13,
              lineHeight: '18px',
              fontWeight: 500,
              color: '#d93025',
            }}
          >
            {error}
          </p>
        ) : null}

        <button
          type="button"
          onClick={() => void handleContinue()}
          disabled={!canContinue}
          style={{
            width: '100%',
            height: 48,
            borderRadius: 9999,
            border: 0,
            backgroundColor: 'var(--text-primary)',
            color: 'var(--bg)',
            fontSize: 15,
            fontWeight: 500,
            fontFamily: 'inherit',
            cursor: canContinue ? 'pointer' : 'default',
            opacity: canContinue ? 1 : 0.4,
            transition: 'opacity 150ms',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
          onMouseEnter={(e) => {
            if (canContinue) e.currentTarget.style.opacity = '0.88';
          }}
          onMouseLeave={(e) => {
            if (canContinue) e.currentTarget.style.opacity = '1';
          }}
        >
          {checkingEmail ? (
            <span
              style={{
                width: 16,
                height: 16,
                border: '2px solid rgba(255,255,255,0.3)',
                borderTopColor: 'var(--bg)',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                display: 'inline-block',
              }}
            />
          ) : null}
          {t('login.continue')}
        </button>
      </div>

      <p
        style={{
          margin: '16px 0 0',
          color: 'var(--text-tertiary)',
          fontSize: 12,
          lineHeight: '18px',
          textAlign: 'center',
        }}
      >
        {t('login.legalPrefix')}{' '}
        <button
          type="button"
          onClick={() => void openMarketingLink('terms', locale)}
          style={legalLinkStyle}
        >
          {t('login.termsLink')}
        </button>{' '}
        {t('login.legalAnd')}{' '}
        <button
          type="button"
          onClick={() => void openMarketingLink('privacy', locale)}
          style={legalLinkStyle}
        >
          {t('login.privacyLink')}
        </button>
        .
      </p>
    </div>
  );
}

const legalLinkStyle: CSSProperties = {
  border: 0,
  padding: 0,
  background: 'transparent',
  color: 'var(--text-primary)',
  font: 'inherit',
  fontWeight: 500,
  textDecoration: 'underline',
  cursor: 'pointer',
};
